import { signal } from '@preact/signals';
import { debounce } from 'es-toolkit/compat';
import { useLocation } from 'preact-iso';
import { useMachine } from 'preact-robot';
import { useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { action, createMachine, state, transition } from 'robot3';
import waitForTimers from 'wait-for-timers';
import { currentEpisode, getCurrentEpisode } from '../../global-state/audio-signals';
import {
  idbStartupCompleteSignal,
  usePodcastsStore,
} from '../../global-state/podcasts';
import { relativeDate } from '../../utils/format-date';
import getThumbUrl from '../../utils/get-thumb';
import { classNames } from '../../utils/helpers';
import { isIOS, isPlaylistSupported } from '../../utils/platform';
import { route } from '../../utils/routing';
import AudioPlayer from '../audio-player';
import BottomNavBar from '../bottom-nav-bar';
import { ButtonBar } from '../button-bar';
import DownloadButton from '../download-button';
import { EpisodeDescription } from '../episode-description';
import { Icon } from '../icon';
import { ListenLaterButton } from '../listen-later-button';
import PlayerBar from '../player-bar';
import DraggablePanel from './draggable-panel';
import { Playlist } from './playlist';
import style from './style.module.scss';

// Simple state machine for expanding / collapsing
const playerPanelStateMachine = createMachine({
  collapsed: state(
    transition(
      'expand',
      'expanding',
      // Reflect state in URL (sharing, reloads, and so if user presses "Back" panel will close)
      action(() => route(location.pathname + '?player=expand'))
    ),
    // Instantly expand to match URL on page load
    // @ts-expect-error not sure why this is a type error, it works fine
    transition('expand-instantly', 'expanded')
  ),
  expanding: state(transition('done', 'expanded')),
  expanded: state(
    transition(
      'collapse',
      'collapsing',
      // Clear state in URL (replace history entry since going forward to open panel may be unexpected)
      action(() => route(location.pathname, true))
    )
  ),
  collapsing: state(transition('done', 'collapsed')),
});

/**
 * An expandable and collapsable podcast player panel
 * Heavily inspired built-in Podcasts app on iOS
 * When expanding/collapsing, we use a FLIP animation to transition between the small podcast
 * logo on the PlayerBar (at the bottom) to the large podcast logo on the expanded panel
 * This creates a sense of fluidity as the panel expands and collapses
 */
const PlayerPanel = ({ isHidden = false }) => {
  const episode = getCurrentEpisode();
  // const panelCommand = usePlayerStore((s) => s.panelCommand);
  const podcast = episode
    ? usePodcastsStore.getState().podcasts[episode.podcastId]
    : undefined;
  const [stateMachine, send] = useMachine(playerPanelStateMachine);
  const [animationStyles, setAnimationStyles] = useState<{
    box?: string;
    panel?: string;
  }>({});
  const [isNavHidden, setNavHidden] = useState(false);
  const { name: state } = stateMachine;
  const thumbUrl = podcast ? getThumbUrl(podcast, 200) : '';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const largeLogoBoxRef = useRef<HTMLDivElement>(null);
  const playerBarLogoBoxRef = useRef<HTMLDivElement>(null);

  // Animate expanding/collapsing animation
  useLayoutEffect(() => {
    if (state === 'collapsed' && scrollContainerRef.current) {
      // Clean up side effects after collapsing
      const element = scrollContainerRef.current.firstElementChild as HTMLElement;
      if (isIOS) {
        element.style.transition = '';
        element.style.transform = '';
        scrollContainerRef.current.style.overflowY = '';
      } else {
        element.style.transition = '';
        element.style.transform = '';
      }
    }

    if (
      !largeLogoBoxRef.current ||
      !playerBarLogoBoxRef.current ||
      !scrollContainerRef.current
    )
      return;

    // Show/hide bottom nav bar
    const navBarDurationMs = 300;

    // Duration of panel & logo box animations
    const durationMs = state === 'expanding' ? 500 : 400;

    if (state !== 'expanding' && state !== 'collapsing') return;

    // FLIP animation of logo box when expanding & collapsing
    const easing = 'ease';
    const tinyRect = playerBarLogoBoxRef.current.getBoundingClientRect();
    const bigRect = largeLogoBoxRef.current.getBoundingClientRect();

    // 1. First position
    const firstRect = state === 'expanding' ? tinyRect : bigRect;

    // 2. Last position
    const lastRect = state === 'expanding' ? bigRect : tinyRect;

    if (state === 'expanding') {
      setNavHidden(true);

      // 3. Invert - move to initial position
      const hiddenPanelOffset =
        scrollContainerRef.current.getBoundingClientRect().height; // translateY(100%);

      const boxStyles = {
        // Set top & left to match final state (same as large logo box)
        top: `${lastRect.top - hiddenPanelOffset}px`,
        left: `${lastRect.left}px`,
        display: 'block',
        // Transform to initial state (same as small player bar logo box)
        transform: [
          `translateY(${firstRect.top - lastRect.top + hiddenPanelOffset}px)`,
          `translateX(${firstRect.left - lastRect.left}px)`,
          `scale(${firstRect.width / lastRect.width})`,
        ].join(' '),
      } as Record<'top' | 'left' | 'display' | 'transform' | 'transition', string>;

      setAnimationStyles({
        box: Object.entries(boxStyles)
          .map(([k, v]) => `${k}: ${v}`)
          .join(';'),
        panel: `transition: transform ${durationMs}ms ${easing}`,
      });

      // 4. Play - remove transform to play animation
      // Note: double rAF required when dragging up (Chrome on Mac in responsive device toolbar mode)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Add transition and remove transform
          boxStyles.transform = 'none';
          boxStyles.transition = `transform ${durationMs * 0.95}ms ${easing} ${
            durationMs * 0.05
          }ms`;

          setAnimationStyles({
            box: Object.entries(boxStyles)
              .map(([k, v]) => `${k}: ${v}`)
              .join(';'),
            panel: `transition: transform ${durationMs}ms ${easing}`,
          });

          setTimeout(() => {
            send('done');
            setAnimationStyles({});
            avoidPullDownToRefreshOnIOS(scrollContainerRef.current);
          }, durationMs);
        });
      });
    }

    if (state === 'collapsing') {
      // 3. Invert - move to initial position
      // const hiddenPanelOffset = window.innerHeight * 0.98; // translateY(100%);
      const boxStyles = {
        // Set top & left to match final state (same as small player bar logo box)
        top: `${lastRect.top}px`,
        left: `${lastRect.left}px`,
        display: 'block',
        // Transform to initial state (same as large logo box)
        transform: [
          `translateY(${firstRect.top - lastRect.top}px)`,
          `translateX(${firstRect.left - lastRect.left}px)`,
        ].join(' '),
      } as Record<'top' | 'left' | 'display' | 'transform' | 'transition', string>;

      setAnimationStyles({
        box: Object.entries(boxStyles)
          .map(([k, v]) => `${k}: ${v}`)
          .join(';'),
        // panel: `transform: translateY(100%); transition: transform ${durationMs}ms ${easing}`,
      });

      // 4. Play - remove transform to play animation
      waitForTimers(['raf', 'raf'], () => {
        // Add transition and change transform to a scale
        boxStyles.transform = `scale(${lastRect.width / firstRect.width})`;
        boxStyles.transition = `transform ${durationMs}ms ${easing}`;

        setAnimationStyles({
          box: Object.entries(boxStyles)
            .map(([k, v]) => `${k}: ${v}`)
            .join(';'),
          panel: `transform: translateY(100%); transition: transform ${
            durationMs * 1.4
          }ms ${easing}`,
        });

        // Slide up nav bar near end of animation
        setTimeout(
          () => {
            setNavHidden(false);
          },
          durationMs - navBarDurationMs - 50
        );

        setTimeout(() => {
          send('done');
          setAnimationStyles({});
        }, durationMs);
      });
    }
  }, [state]);

  // Update panel to match URL state
  const { query, route, path } = useLocation();
  const isUrlExpanded = query.player === 'expand';
  const isInitialEpisodeSelected = idbStartupCompleteSignal.value;
  useLayoutEffect(() => {
    const isPanelExpanded = state === 'expanded' || state === 'expanding';
    if (isPanelExpanded === isUrlExpanded) return;

    // Note that if it's currently expanding or collapsing, these commands will
    // be ignored, but I figure that's a real edge case. We only really want URL
    // to drive updates from a stable place (e.g. first load, back/forward, etc.)
    if (isUrlExpanded) {
      if (!isInitialEpisodeSelected) return;
      if (currentEpisode.peek()) {
        // Expand w/out animation b/c this is initial page state
        send('expand-instantly');
      } else {
        // Clear URL state since there's no episode
        route(path, true);
      }
    } else {
      send('collapse');
    }
  }, [isUrlExpanded, isInitialEpisodeSelected]);

  const panelStyles =
    animationStyles.panel ||
    (state === 'collapsed' || state === 'expanding'
      ? 'transform: translateY(100%)'
      : '');

  const veilStyles = state === 'expanding' || state === 'expanded' ? 'opacity: 1' : '';

  // On landing pages
  if (isHidden) {
    return <></>;
  }

  return (
    <>
      <PlayerBar
        ref={playerBarLogoBoxRef}
        isLogoVisible={state !== 'expanding' && state !== 'collapsing'}
        expand={() => (episode ? send('expand') : {})}
        thumbUrl={thumbUrl}
      />
      <DraggablePanel
        panelStyles={panelStyles}
        veilStyles={veilStyles}
        isExpanded={state === 'expanded'}
        ref={scrollContainerRef}
        collapse={() => send('collapse')}
        avoidPullDownToRefreshOnIOS={avoidPullDownToRefreshOnIOS}
      >
        <div
          class={classes(['__logo-box', `__logo-box--${state}`])}
          style={thumbUrl ? { backgroundImage: `url(${thumbUrl})` } : {}}
          ref={largeLogoBoxRef}
        ></div>
        {podcast ? (
          <>
            <h2 class="text-center">{episode?.title}</h2>
            {/* <p>{podcast.name}</p> */}
            {episode && (
              <>
                <p>{relativeDate(episode.pubDate)}</p>
                <div class="my-1 flex items-center gap-2.5">
                  <ListenLaterButton episode={episode} />
                  <DownloadButton episode={episode} />
                </div>
              </>
            )}
            <AudioPlayer />
            <PlaylistTabs />
          </>
        ) : (
          ''
        )}
      </DraggablePanel>
      <div
        style={[animationStyles.box, thumbUrl && `background-image: url(${thumbUrl})`]
          .filter(Boolean)
          .join(';')}
        class={classes('__logo-box-animated')}
      ></div>
      <BottomNavBar isHidden={isNavHidden}></BottomNavBar>
    </>
  );
};

// Save outside of component to restore when page is back in view
const belowPlayerTab = signal<'show_notes' | 'up_next'>('show_notes');

function PlaylistTabs() {
  const episode = getCurrentEpisode();

  if (!episode) return <></>;

  if (!isPlaylistSupported) return <EpisodeDescription episode={episode} />;

  const tabButtons = useMemo(
    () =>
      [
        {
          id: 'show_notes',
          children: 'Show Notes',
        },
        {
          id: 'up_next',
          children: (
            <span class="flex items-center gap-3">
              Up Next
              <Icon sprite="queue-top" class="-ml-1 h-[26px] w-[24px] text-gray-400" />
            </span>
          ),
        },
      ] as const,
    []
  );

  return (
    <>
      <nav
        class={classNames(
          'sticky top-[var(--safe-area-top)] -mx-8 flex h-[var(--header-ui-height)] w-[calc(100%+64px)] items-center',
          'bg-slate-100/85 backdrop-blur'
        )}
        role="tablist"
      >
        <ButtonBar
          buttons={tabButtons}
          selectedId={belowPlayerTab.value}
          onSelect={(tab) => {
            belowPlayerTab.value = tab;
          }}
          buttonClassName="h-9 [&_span]:rounded-b-none [&_span]:rounded-t-lg px-4"
        />
      </nav>
      {/* 50px for sticky player bar, 6rem for bottom padding on player panel content, 190 for audio player */}
      <div className="min-h-[calc(100vh-50px-6rem-190px)] w-full">
        {belowPlayerTab.value === 'show_notes' && (
          <EpisodeDescription episode={episode} />
        )}
        {belowPlayerTab.value === 'up_next' && <Playlist />}
      </div>
    </>
  );
}

function classes(ems) {
  if (!Array.isArray(ems)) ems = [ems];
  return ems
    .filter((em) => typeof em === 'string')
    .map((em) => style['player-panel' + em])
    .join(' ');
}

// Set scrollTop to 1px on iOS to prevent "pull down to refresh" behavior
const avoidPullDownToRefreshOnIOS = debounce(
  (scrollContainer) => {
    if (!isIOS) return;

    if (scrollContainer.scrollTop !== 0) return;

    // Set scrollTop to 1 so that pulling down pulls on panel, and does not
    // trigger a pull-to-refresh on the page
    scrollContainer.scrollTop = 1;
    if (
      scrollContainer.scrollTop === 0 &&
      scrollContainer.scrollHeight > scrollContainer.clientHeight
    ) {
      console.warn('Failed to set scrollTop');
    }
  },
  80,
  { leading: true, trailing: true }
);

export default PlayerPanel;
